/**
 * Ensures dynamically injected iframes have title attributes for screen readers.
 * Shopify's preview bar (#PBarNextFrame) and some apps inject iframes without titles.
 */

const TITLES = {
  previewBar:
    document.documentElement.dataset.iframeTitlePreviewBar ??
    'Shopify theme preview toolbar',
  fallback:
    document.documentElement.dataset.iframeTitleFallback ?? 'Embedded content',
};

const PREVIEW_BAR_IDS = new Set(['PBarNextFrame', 'preview-bar-iframe']);

/**
 * @param {HTMLIFrameElement} iframe
 * @returns {string}
 */
function getIframeTitle(iframe) {
  if (PREVIEW_BAR_IDS.has(iframe.id)) {
    return TITLES.previewBar;
  }

  const src = iframe.getAttribute('src') ?? '';
  if (src.includes('shopify') && /preview|admin.?bar|bar/i.test(src)) {
    return TITLES.previewBar;
  }

  return TITLES.fallback;
}

/**
 * @param {HTMLIFrameElement} iframe
 */
function ensureIframeTitle(iframe) {
  const existing = iframe.getAttribute('title')?.trim();
  if (existing) {
    return;
  }

  iframe.setAttribute('title', getIframeTitle(iframe));
}

const iframes = document.getElementsByTagName('iframe');
let scanQueued = false;

function scanIframes() {
  scanQueued = false;
  for (const iframe of iframes) {
    ensureIframeTitle(iframe);
  }
}

// Large DOM insertions (Algolia grids, header hydration, app widgets) fire many mutations.
// Coalesce them into one idle pass over the live iframe list instead of querying every added subtree.
function queueScan() {
  if (scanQueued) return;
  scanQueued = true;

  if ('requestIdleCallback' in window) {
    requestIdleCallback(scanIframes, { timeout: 1000 });
  } else {
    setTimeout(scanIframes, 200);
  }
}

function init() {
  scanIframes();

  const observer = new MutationObserver(queueScan);
  observer.observe(document.documentElement, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
